import { Box, Button, ListItemIcon, MenuItem } from "@mui/material";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { getColumns } from "./columnNames/NozzleColumns";
import { MaterialReactTable } from "material-react-table";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";

const NOZZLE_COLUMN_WEIGHTS = Object.freeze({
  UID: 1.35,

  Mark: 0.72,
  Service: 0.82,
  Size: 0.62,
  Type: 0.7,

  "Flange Rating": 0.88,
  "Sch/Thk": 0.82,
  "Weld Detail": 0.88,

  "Nozzle Weld (A)": 0.92,
  "Inside Weld (B)": 0.92,
  "Pad Weld (C)": 0.9,

  "Outside Proj": 0.82,
  "Inside Proj": 0.82,

  "Pad OD": 0.72,
  "Pad Thk": 0.72,
});

/**
 * Generated MRT row-actions column.
 */
const NOZZLE_ROW_ACTION_WEIGHT = 0.5;
const NOZZLE_HEADER_HEIGHT = 68;
const NOZZLE_ROW_HEIGHT = 40;

const getNozzleColumnId = (column) =>
  String(column?.id || column?.accessorKey || "");
const getNozzleColumnWeight = (column, isSplitView) => {
  const id = getNozzleColumnId(column);

  /*
   * Give Service extra room only when Nozzles
   * shares the screen with another table.
   */
  if (id === "Service" && isSplitView) {
    return 1.05;
  }

  return NOZZLE_COLUMN_WEIGHTS[id] ?? 1;
};
const NOZZLE_COLUMN_BORDER =
  "1px solid var(--pv-table-border, rgba(170, 180, 198, 0.6))";

// Themed in src/styles/theme.js (MuiCssBaseline -> html); the value after the
// comma is the light-theme fallback.
const NOZZLE_HEADER_BACKGROUND =
  "var(--pv-table-header-bg, hsla(250, 86%, 97%, 1))";
const NOZZLE_HEADER_TEXT = "var(--pv-table-header-text, inherit)";

// 0.8rem, matching EstimationTab/BomTable below this table on the tab.
const NOZZLE_FONT_SIZE = 12.8;

const applyResponsiveNozzleColumnWidth = (
  column,
  widthPx,
  headerFontSize,
  bodyFontSize,
) => {
  const safeWidth = Math.max(1, widthPx);

  const currentHeadProps =
    column?.muiTableHeadCellProps &&
    typeof column.muiTableHeadCellProps === "object"
      ? column.muiTableHeadCellProps
      : {};

  const currentBodyProps =
    column?.muiTableBodyCellProps &&
    typeof column.muiTableBodyCellProps === "object"
      ? column.muiTableBodyCellProps
      : {};

  return {
    ...column,

    /*
     * Final width wins over all old sizes from NozzleColumns.
     */
    size: safeWidth,
    minSize: safeWidth,
    maxSize: safeWidth,
    grow: false,

    muiTableHeadCellProps: {
      ...currentHeadProps,

      sx: {
        ...(currentHeadProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        /*
         * Restore the original clean header appearance.
         */
        height: `${NOZZLE_HEADER_HEIGHT}px !important`,
        minHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
        maxHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,

        borderRight: `${NOZZLE_COLUMN_BORDER} !important`,
        borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,

        backgroundColor: `${NOZZLE_HEADER_BACKGROUND} !important`,
        color: `${NOZZLE_HEADER_TEXT} !important`,

        textAlign: "center",
        verticalAlign: "middle",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        overflow: "hidden",

        px: {
          xs: 0,
          sm: 0.1,
          md: 0.25,
          lg: 0.4,
        },

        py: 0.5,

        fontSize: `${headerFontSize}px !important`,
        fontWeight: "400 !important",
        lineHeight: "1.1 !important",

        "& .Mui-TableHeadCell-Content": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          height: "100%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
        },

        "& .Mui-TableHeadCell-Content-Labels": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
        },

        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          textAlign: "center",

          whiteSpace: "normal !important",
          overflowWrap: "normal",
          wordBreak: "normal",

          overflow: "hidden !important",
          textOverflow: "ellipsis",

          fontSize: `${headerFontSize}px !important`,
          fontWeight: "400 !important",
          lineHeight: "1.1 !important",
        },

        /*
         * Custom Headers such as Nozzle Weld / Inside Weld
         * inherit exactly the same typography.
         */
        "& .Mui-TableHeadCell-Content-Wrapper *": {
          fontSize: "inherit !important",
          fontWeight: "inherit !important",
          lineHeight: "inherit !important",
        },

        "& .Mui-TableHeadCell-Content": {
          width: "100% !important",
          minWidth: "0 !important",
          maxWidth: "100% !important",

          display: "flex !important",
          alignItems: "center !important",
          justifyContent: "center !important",
        },

        "& .Mui-TableHeadCell-Content-Labels": {
          width: "100% !important",
          minWidth: "0 !important",
          maxWidth: "100% !important",

          flex: "1 1 100% !important",

          display: "flex !important",
          alignItems: "center !important",
          justifyContent: "center !important",
        },

        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100% !important",
          minWidth: "0 !important",
          maxWidth: "100% !important",

          flex: "1 1 100% !important",

          display: "block !important",

          textAlign: "center !important",

          padding: "0 !important",
          margin: "0 !important",
        },

        "& .Mui-TableHeadCell-Content-Wrapper > *": {
          width: "100% !important",
          minWidth: "0 !important",
          maxWidth: "100% !important",

          textAlign: "center !important",

          marginLeft: "auto !important",
          marginRight: "auto !important",
        },
      },
    },

    muiTableBodyCellProps: {
      ...currentBodyProps,

      sx: {
        ...(currentBodyProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

        overflow: "hidden",

        p: 0,

        fontSize: `${bodyFontSize}px !important`,
        fontWeight: "400 !important",
        height: `${NOZZLE_ROW_HEIGHT}px !important`,
        minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,
        maxHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

        display: "flex",
        alignItems: "center",

        "& .Mui-TableBodyCell-Content-Wrapper": {
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",

          overflow: "hidden",

          textOverflow: "ellipsis",

          fontSize: `${bodyFontSize}px !important`,
          fontWeight: "400 !important",
        },
      },
    },
  };
};

const InputTable = (props) => {
  const {
    handleCreateNewRow,
    handleDeleteRow,
    handleSaveCell,
    fields,
    isSplitView = false,
  } = props;
  const { getValues } = useFormContext();

  const tableViewportRef = useRef(null);

  const [tableViewportWidth, setTableViewportWidth] = useState(1000);

  useLayoutEffect(() => {
    let frameId = null;

    const updateWidth = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const element = tableViewportRef.current;

        if (!element) return;

        const rect = element.getBoundingClientRect();

        /*
         * CRITICAL:
         * Do not trust rect.width because an ancestor may still be wider
         * than the actual browser viewport.
         *
         * Calculate only the space that is REALLY visible on screen.
         */
        const viewportWidth = document.documentElement.clientWidth;

        const parent = element.parentElement;
        const parentRect = parent?.getBoundingClientRect();

        const visibleLeft = Math.max(rect.left, 0);

        const visibleRight = Math.min(
          viewportWidth,
          parentRect?.right ?? viewportWidth,
        );

        const nextWidth = Math.max(
          1,
          Math.floor(visibleRight - visibleLeft - 1),
        );

        setTableViewportWidth((previous) =>
          previous === nextWidth ? previous : nextWidth,
        );
      });
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;

    const element = tableViewportRef.current;

    if (resizeObserver && element) {
      resizeObserver.observe(element);

      if (element.parentElement) {
        resizeObserver.observe(element.parentElement);
      }
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", updateWidth);

      resizeObserver?.disconnect();
    };
  }, []);

  // Fixed rather than scaled to the viewport, so this table reads at the same
  // size as the BOM table below it (EstimationTab/BomTable: 0.8rem).
  const tableHeaderFontSize = NOZZLE_FONT_SIZE;
  const tableBodyFontSize = NOZZLE_FONT_SIZE;

  const rawColumns = useMemo(
    () => getColumns(fields, getValues),
    [fields, getValues],
  );

  const { responsiveColumns, rowActionsWidth } = useMemo(() => {
    const columnsWeight = rawColumns.reduce(
      (sum, column) => sum + getNozzleColumnWeight(column, isSplitView),
      0,
    );

    const totalWeight = columnsWeight + NOZZLE_ROW_ACTION_WEIGHT;

    /*
     * Safety pixels for borders/sub-pixel rounding.
     */
    const usableWidth = Math.max(1, tableViewportWidth - 3);

    const nextColumns = rawColumns.map((column) => {
      const weight = getNozzleColumnWeight(column, isSplitView);
      const width = (weight / totalWeight) * usableWidth;
      return applyResponsiveNozzleColumnWidth(
        column,
        width,
        tableHeaderFontSize,
        tableBodyFontSize,
      );
    });

    const actionWidth = (NOZZLE_ROW_ACTION_WEIGHT / totalWeight) * usableWidth;

    return {
      responsiveColumns: nextColumns,
      rowActionsWidth: Math.max(1, actionWidth),
    };
  }, [
    rawColumns,
    tableViewportWidth,
    tableHeaderFontSize,
    tableBodyFontSize,
    isSplitView,
  ]);

  return (
    <Box
      ref={tableViewportRef}
      sx={{
        width: `${tableViewportWidth}px`,
        minWidth: 0,
        maxWidth: `${tableViewportWidth}px`,

        boxSizing: "border-box",

        overflowX: "hidden",
        overflowY: "visible",
      }}
    >
      <MaterialReactTable
        columns={responsiveColumns}
        data={fields}
        enableTopToolbar={false}
        layoutMode="grid-no-grow"
        enableSorting={false}
        editDisplayMode="table"
        enableEditing
        enableRowActions
        positionActionsColumn="last"
        enableColumnActions={false}
        defaultColumn={{
          size: 1,
          minSize: 1,
          maxSize: 100000,
          grow: false,
        }}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "Actions",

            size: rowActionsWidth,
            minSize: rowActionsWidth,
            maxSize: rowActionsWidth,
            grow: false,

            enableSorting: false,
            enableColumnActions: false,

            muiTableHeadCellProps: {
              sx: {
                boxSizing: "border-box",

                width: `${rowActionsWidth}px !important`,
                minWidth: `${rowActionsWidth}px !important`,
                maxWidth: `${rowActionsWidth}px !important`,

                flex: `0 0 ${rowActionsWidth}px !important`,
                flexBasis: `${rowActionsWidth}px !important`,
                flexGrow: "0 !important",
                flexShrink: "0 !important",

                height: `${NOZZLE_HEADER_HEIGHT}px !important`,
                minHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
                maxHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,

                backgroundColor: `${NOZZLE_HEADER_BACKGROUND} !important`,
                color: `${NOZZLE_HEADER_TEXT} !important`,

                borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

                borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,

                p: "0 !important",

                textAlign: "center",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                overflow: "hidden",

                fontSize: `${tableHeaderFontSize}px !important`,

                fontWeight: "400 !important",
                lineHeight: "1.1 !important",

                // "& .Mui-TableHeadCell-Content": {
                //   width: "100%",
                //   minWidth: "0 !important",
                //   maxWidth: "100%",

                //   height: "100%",

                //   display: "flex",
                //   alignItems: "center",
                //   justifyContent: "center",

                //   overflow: "hidden",
                // },

                // "& .Mui-TableHeadCell-Content-Wrapper": {
                //   width: "100%",
                //   minWidth: "0 !important",
                //   maxWidth: "100%",

                //   textAlign: "center",

                //   whiteSpace: "normal !important",

                //   overflow: "hidden !important",

                //   fontSize: `${tableHeaderFontSize}px !important`,

                //   fontWeight: "400 !important",
                //   lineHeight: "1.1 !important",
                // },

                "& .Mui-TableHeadCell-Content": {
                  width: "100% !important",
                  minWidth: "0 !important",
                  maxWidth: "100% !important",
                  height: "100%",

                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",

                  padding: "0 !important",
                  margin: "0 !important",

                  overflow: "hidden",
                },

                "& .Mui-TableHeadCell-Content-Labels": {
                  width: "100% !important",
                  minWidth: "0 !important",
                  maxWidth: "100% !important",

                  flex: "1 1 100% !important",

                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",

                  padding: "0 !important",
                  margin: "0 !important",

                  overflow: "hidden",
                },

                "& .Mui-TableHeadCell-Content-Wrapper": {
                  width: "100% !important",
                  minWidth: "0 !important",
                  maxWidth: "100% !important",

                  flex: "1 1 100% !important",

                  display: "block !important",

                  padding: "0 !important",
                  margin: "0 !important",

                  textAlign: "center !important",

                  whiteSpace: "normal !important",

                  overflow: "hidden !important",

                  fontSize: `${tableHeaderFontSize}px !important`,
                  fontWeight: "400 !important",
                  lineHeight: "1.1 !important",
                },

                "& .Mui-TableHeadCell-Content-Wrapper > *": {
                  width: "100% !important",
                  minWidth: "0 !important",
                  maxWidth: "100% !important",

                  marginLeft: "auto !important",
                  marginRight: "auto !important",

                  textAlign: "center !important",
                },

                "& .Mui-TableHeadCell-Content-Labels": {
                  width: "100%",
                  minWidth: "0 !important",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  overflow: "hidden",
                },
              },
            },

            muiTableBodyCellProps: {
              sx: {
                boxSizing: "border-box",

                width: `${rowActionsWidth}px !important`,
                minWidth: `${rowActionsWidth}px !important`,
                maxWidth: `${rowActionsWidth}px !important`,

                flex: `0 0 ${rowActionsWidth}px !important`,
                flexBasis: `${rowActionsWidth}px !important`,
                flexGrow: "0 !important",
                flexShrink: "0 !important",

                borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

                borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,

                p: 0,

                overflow: "hidden",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: `${NOZZLE_ROW_HEIGHT}px !important`,
                minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,
                maxHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

                display: "flex",
                alignItems: "center",

                "& .Mui-TableBodyCell-Content-Wrapper": {
                  width: "100%",
                  minWidth: 0,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  overflow: "hidden",
                },

                "& .MuiIconButton-root": {
                  minWidth: 0,

                  p: {
                    xs: 0,
                    sm: 0.1,
                    md: 0.25,
                  },
                },
              },
            },
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            boxSizing: "border-box",

            height: `${NOZZLE_HEADER_HEIGHT}px !important`,
            minHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
            maxHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
            backgroundColor: `${NOZZLE_HEADER_BACKGROUND} !important`,
            color: `${NOZZLE_HEADER_TEXT} !important`,

            borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

            borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,

            textAlign: "center",

            overflow: "hidden",

            p: 0,

            fontSize: `${tableHeaderFontSize}px !important`,

            fontWeight: "400 !important",

            "& .Mui-TableHeadCell-Content": {
              width: "100%",
              minWidth: 0,
              height: "100%",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              overflow: "hidden",
            },

            "& .Mui-TableHeadCell-Content-Wrapper": {
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",

              textAlign: "center",

              whiteSpace: "normal",

              overflow: "hidden",

              fontSize: `${tableHeaderFontSize}px !important`,

              fontWeight: "400 !important",
            },
          },
        }}
        initialState={{ density: "compact" }}
        muiTableProps={{
          size: "small",

          sx: {
            width: `${tableViewportWidth}px !important`,
            minWidth: `${tableViewportWidth}px !important`,
            maxWidth: `${tableViewportWidth}px !important`,

            overflow: "hidden !important",

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              fontSize: `${tableBodyFontSize}px !important`,

              borderRadius: 0,

              overflow: "hidden",
            },

            "& .MuiButtonBase-root": {
              fontSize: `${tableBodyFontSize}px !important`,
            },
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            height: `${NOZZLE_ROW_HEIGHT}px !important`,
            minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,
            maxHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",

            minWidth: "0 !important",

            p: 0,

            overflow: "hidden",

            borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

            borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,

            fontSize: `${tableBodyFontSize}px !important`,

            fontWeight: "400 !important",

            "& .Mui-TableBodyCell-Content-Wrapper": {
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",

              overflow: "hidden",

              fontSize: `${tableBodyFontSize}px !important`,

              fontWeight: "400 !important",
            },
          },
        }}
        muiTableContainerProps={{
          sx: {
            width: `${tableViewportWidth}px !important`,
            minWidth: `${tableViewportWidth}px !important`,
            maxWidth: `${tableViewportWidth}px !important`,
            boxSizing: "border-box",
            overflowX: "hidden !important",
            overscrollBehaviorX: "none",
            "& *": {
              boxSizing: "border-box",
            },

            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: `${NOZZLE_HEADER_BACKGROUND} !important`,
              color: `${NOZZLE_HEADER_TEXT} !important`,

              borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

              borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,
            },

            "& .MuiTable-root": {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,
            },

            "& .MuiTableHead-root, & .MuiTableBody-root": {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",
            },

            "& .MuiTableRow-root": {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",
            },

            "& .MuiTableCell-root": {
              boxSizing: "border-box",

              minWidth: "0 !important",

              overflow: "hidden",
            },

            /*
             * Editing TextField / Select must never force
             * the column to become wider.
             */
            "& .MuiTextField-root, & .MuiFormControl-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",
            },

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden",
            },

            "& .MuiInputBase-input": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              boxSizing: "border-box",

              fontSize: `${tableBodyFontSize}px !important`,
            },

            "& .MuiSelect-select": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden",
              textOverflow: "ellipsis",

              fontSize: `${tableBodyFontSize}px !important`,
            },

            "& .MuiSvgIcon-root": {
              fontSize: "clamp(0.6rem, 1vw, 1.15rem) !important",
            },

            "& .MuiTableCell-root": {
              minWidth: "0 !important",
              overflow: "hidden",

              borderRight: `${NOZZLE_COLUMN_BORDER} !important`,
            },

            "& .Mui-TableBodyCell-Content-Wrapper": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden",
              textOverflow: "ellipsis",
            },

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden",
            },

            "& .MuiInputBase-input": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden",
              textOverflow: "ellipsis",
            },

            "& .MuiSelect-select": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              overflow: "hidden !important",
              textOverflow: "ellipsis",
            },
          },
        }}
        muiTableHeadRowProps={{
          sx: {
            display: "flex !important",

            width: `${tableViewportWidth}px !important`,
            minWidth: `${tableViewportWidth}px !important`,
            maxWidth: `${tableViewportWidth}px !important`,

            height: `${NOZZLE_HEADER_HEIGHT}px !important`,
            minHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
            maxHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,

            alignItems: "stretch",

            overflow: "hidden",

            backgroundColor: NOZZLE_HEADER_BACKGROUND,
            color: NOZZLE_HEADER_TEXT,

            "& .MuiTableCell-root": {
              height: `${NOZZLE_HEADER_HEIGHT}px !important`,
              minHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,
              maxHeight: `${NOZZLE_HEADER_HEIGHT}px !important`,

              boxSizing: "border-box",

              borderRight: `${NOZZLE_COLUMN_BORDER} !important`,

              borderBottom: `${NOZZLE_COLUMN_BORDER} !important`,
            },
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            display: "flex !important",

            width: `${tableViewportWidth}px !important`,
            minWidth: `${tableViewportWidth}px !important`,
            maxWidth: `${tableViewportWidth}px !important`,

            height: `${NOZZLE_ROW_HEIGHT}px !important`,
            minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,
            maxHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

            alignItems: "stretch",

            overflow: "hidden",

            "& .MuiTableCell-root": {
              height: `${NOZZLE_ROW_HEIGHT}px !important`,
              minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,
              maxHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

              boxSizing: "border-box",
              overflow: "hidden",
            },
          },
        }}
        muiBottomToolbarProps={{
          sx: {
            "& .css-1llu0od": {
              padding: "0rem 0.5rem",
            },
          },
        }}
        muiEditTextFieldProps={({ cell }) => ({
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value, "DataGridView1");
          },

          fullWidth: true,

          sx: {
            width: "100%",
            minWidth: "0 !important",
            maxWidth: "100%",

            overflow: "hidden",

            borderRight: NOZZLE_COLUMN_BORDER,

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",
            },

            "& .MuiInputBase-input": {
              width: "100%",
              minWidth: "0 !important",

              boxSizing: "border-box",

              padding: {
                xs: "2px 0",
                sm: "3px 1px",
                md: "4px 2px",
              },

              textAlign: "center",

              fontSize: `${tableBodyFontSize}px !important`,
            },

            "& .MuiSelect-select": {
              minWidth: "0 !important",

              px: {
                xs: 0,
                md: 0.5,
              },

              textAlign: "center",

              fontSize: `${tableBodyFontSize}px !important`,
            },

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              height: `${NOZZLE_ROW_HEIGHT}px !important`,
              minHeight: `${NOZZLE_ROW_HEIGHT}px !important`,

              overflow: "hidden",
            },

            "& .MuiInputBase-input": {
              width: "100%",
              minWidth: "0 !important",

              boxSizing: "border-box",

              height: `${NOZZLE_ROW_HEIGHT}px`,
              padding: "0 2px",

              textAlign: "center",

              fontSize: `${tableBodyFontSize}px !important`,
            },
            "& .MuiSelect-select": {
              height: `${NOZZLE_ROW_HEIGHT}px !important`,

              display: "flex",
              alignItems: "center",

              boxSizing: "border-box",

              py: "0 !important",

              textAlign: "center",
            },
          },
        })}
        renderBottomToolbarCustomActions={() => (
          <Button
            color="primary"
            onClick={() => handleCreateNewRow()}
            variant="contained"
          >
            Add row
          </Button>
        )}
        renderRowActionMenuItems={({ row }) => [
          <MenuItem
            key={0}
            sx={{ m: 0 }}
            onClick={() => handleDeleteRow(row.index)}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            Delete
          </MenuItem>,
        ]}
      />
    </Box>
  );
};

export default InputTable;
